import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip
} from '@mui/material';
import {
  LocalShipping as DeliveryIcon,
  MonetizationOn as MoneyIcon,
  Receipt as ReceiptIcon,
  CreditCard as CardIcon,
  AccountBalance as DebtIcon,
  PhoneIphone as PhoneIphoneIcon,
  Engineering as EngineeringIcon,
  Undo as RefundIcon
} from '@mui/icons-material';
import {
  updateMaintenanceFinalCost,
  recordPayment,
  createDebtRecord,
  calculateCostSummary
} from '../../services/maintenanceCostService';
import { updateMaintenanceStatus } from '../../services/maintenanceService';
import { getFirestore, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { hybridWhatsAppService } from '../../services/hybridWhatsAppService';
import { formatCurrency } from '../../utils/numberUtils';
import { MaintenanceRecord, MaintenancePaymentMethod, MaintenanceStatus } from '../../utils/types';
import { getTechniciansByBranch } from '../../services/technicianService';

interface DeliveryPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

const DeliveryPaymentDialog: React.FC<DeliveryPaymentDialogProps> = ({
  open,
  onClose,
  maintenance,
  userId,
  userName,
  onSuccess
}) => {
  const [finalCost, setFinalCost] = useState<number>(0);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<MaintenancePaymentMethod>('cash');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletNumber, setWalletNumber] = useState('');

  // حالات جديدة
  const [isReturn, setIsReturn] = useState(false);
  const [isRejectedReturn, setIsRejectedReturn] = useState(false);
  const [newTechnicianId, setNewTechnicianId] = useState<string>('');
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [hasExtraWages, setHasExtraWages] = useState(false);
  const [extraWagesAmount, setExtraWagesAmount] = useState<number>(0);

  const electronicMethods = ['vodafone_wallet', 'instapay', 'etisalat_wallet'];

  // حساب ملخص التكلفة
  const costSummary = calculateCostSummary(maintenance);

  // تحديث القيم عند فتح النافذة
  useEffect(() => {
    if (open && maintenance) {
      const estimatedCost = maintenance.estimatedCost || maintenance.initialCost || 0;
      setFinalCost(estimatedCost);
      setPaymentAmount(estimatedCost);
      setPaymentMethod('cash');
      setError(null);
      setIsReturn(false);
      setIsRejectedReturn(false);
      setNewTechnicianId(maintenance.technicianId || '');
      setHasExtraWages(false);
      setExtraWagesAmount(0);

      // تحميل الفنيين
      loadTechnicians();
    }
  }, [open, maintenance]);

  // تحميل الفنيين
  const loadTechnicians = async () => {
    try {
      if (maintenance?.branchId) {
        const techniciansList = await getTechniciansByBranch(maintenance.branchId);
        setTechnicians(techniciansList);
      }
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const partsCost = maintenance.usedParts?.reduce((sum, part) =>
    sum + (part.purchasePrice * part.quantity), 0) || 0;

  // حساب التكلفة النهائية مع الأجور الإضافية
  const totalFinalCost = finalCost + (hasExtraWages ? extraWagesAmount : 0);
  const laborCost = Math.max(0, totalFinalCost - partsCost);

  // حساب المبلغ المتبقي بناءً على طريقة الدفع
  const actualPaymentAmount = isReturn ? 0 : (paymentMethod === 'debt' ? 0 : paymentAmount);
  const remainingAfterPayment = Math.max(0, totalFinalCost - actualPaymentAmount);

  const handleSubmit = async () => {
    // التحقق من صحة البيانات
    if (!isReturn && totalFinalCost <= 0) {
      setError('يجب إدخال تكلفة نهائية أكبر من صفر');
      return;
    }

    if (!isReturn && actualPaymentAmount < 0) {
      setError('مبلغ الدفع لا يمكن أن يكون سالباً');
      return;
    }

    if (!isReturn && actualPaymentAmount > totalFinalCost) {
      setError('مبلغ الدفع لا يمكن أن يتجاوز التكلفة النهائية');
      return;
    }

    if (!isReturn && electronicMethods.includes(paymentMethod) && !walletNumber) {
      setError('يرجى إدخال رقم المحفظة أو الحساب المحول عليه');
      return;
    }

    if (hasExtraWages && extraWagesAmount <= 0) {
      setError('يجب إدخال مبلغ أجور الزيادة أكبر من صفر');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // إذا كان مرتجع، تغيير الحالة إلى cancelled فقط
      if (isReturn) {
        await updateMaintenanceStatus(maintenance.id, MaintenanceStatus.CANCELLED);

        // تحديث الفني إذا تم تغييره
        if (newTechnicianId && newTechnicianId !== maintenance.technicianId) {
          const db = getFirestore();
          const maintenanceRef = doc(db, 'maintenance', maintenance.id);
          await updateDoc(maintenanceRef, {
            technicianId: newTechnicianId,
            technicianName: technicians.find(t => t.id === newTechnicianId)?.name || '',
            returnReason: isRejectedReturn ? 'rejected' : 'returned',
            isRejectedReturn: isRejectedReturn,
            notes: maintenance.notes ?
              `${maintenance.notes}\n\n${isRejectedReturn ? 'مرتجع مع رفض الجهاز' : 'مرتجع عادي'}` :
              (isRejectedReturn ? 'مرتجع مع رفض الجهاز' : 'مرتجع عادي'),
            updatedAt: Timestamp.now()
          });
        } else {
          // إضافة معلومات الإرجاع حتى لو لم يتم تغيير الفني
          const db = getFirestore();
          const maintenanceRef = doc(db, 'maintenance', maintenance.id);
          await updateDoc(maintenanceRef, {
            returnReason: isRejectedReturn ? 'rejected' : 'returned',
            isRejectedReturn: isRejectedReturn,
            notes: maintenance.notes ?
              `${maintenance.notes}\n\n${isRejectedReturn ? 'مرتجع مع رفض الجهاز' : 'مرتجع عادي'}` :
              (isRejectedReturn ? 'مرتجع مع رفض الجهاز' : 'مرتجع عادي'),
            updatedAt: Timestamp.now()
          });
        }

        onSuccess();
        onClose();
        return;
      }

      // 1. تحديد التكلفة النهائية وتحديث حالة الصيانة إلى "تم التسليم"
      await updateMaintenanceFinalCost(
        maintenance.id,
        totalFinalCost,
        userId,
        partsCost,
        paymentMethod // تمرير طريقة الدفع الأصلية
      );

      // تحديث الفني إذا تم تغييره
      if (newTechnicianId && newTechnicianId !== maintenance.technicianId) {
        const db = getFirestore();
        const maintenanceRef = doc(db, 'maintenance', maintenance.id);
        await updateDoc(maintenanceRef, {
          technicianId: newTechnicianId,
          updatedAt: Timestamp.now()
        });
      }

      // 2. تسجيل الدفعة إذا كان هناك مبلغ مدفوع
      if (actualPaymentAmount > 0) {
        let notes = `دفعة عند التسليم - `;
        let actualPaymentMethod = paymentMethod;

        // تحديد طريقة الدفع والملاحظات
        switch (paymentMethod) {
          case 'cash':
            notes += 'نقدي';
            break;
          case 'debt':
            // إذا كان نوع الدفع "دين" لكن هناك مبلغ مدفوع، فهو دفع جزئي نقدي
            actualPaymentMethod = 'cash';
            notes += 'نقدي جزئي';
            break;
          case 'partial':
            actualPaymentMethod = 'cash';
            notes += 'جزئي';
            break;
          case 'vodafone_wallet':
            notes += `محفظة فودافون (${walletNumber})`;
            break;
          case 'instapay':
            notes += `انستاباي (${walletNumber})`;
            break;
          case 'etisalat_wallet':
            notes += `اتصالات كاش (${walletNumber})`;
            break;
          default:
            actualPaymentMethod = 'cash';
            notes += 'نقدي';
        }

        await recordPayment(
          maintenance.id,
          actualPaymentAmount,
          actualPaymentMethod,
          userId,
          userName,
          notes
        );
      }

      // 3. إنشاء سجل دين تلقائياً إذا كان هناك مبلغ متبقي (بغض النظر عن طريقة الدفع)
      if (remainingAfterPayment > 0) {
        console.log('💳 إنشاء دين تلقائي للمبلغ المتبقي:', {
          remainingAmount: remainingAfterPayment,
          maintenanceId: maintenance.id,
          customerId: maintenance.customerId,
          paymentMethod: paymentMethod
        });

        try {
          // تمرير المبلغ المتبقي مباشرة لضمان الدقة
          await createDebtRecord(maintenance.id, userId, remainingAfterPayment);
          console.log('✅ تم إنشاء سجل الدين بنجاح للمبلغ:', remainingAfterPayment);
        } catch (debtError) {
          console.error('❌ خطأ في إنشاء سجل الدين:', debtError);
          // لا نريد إيقاف العملية إذا فشل إنشاء الدين
          console.warn('⚠️ تم تسليم الجهاز لكن فشل في إنشاء سجل الدين');
        }
      }

      // 4. إرسال رسالة واتساب للعميل بإشعار التسليم
      try {
        // إرسال رسالة واتساب فقط إذا كان مفعلاً
        try {
          const { getSystemSettings } = await import('../../services/systemSettingsService');
          const settings = await getSystemSettings();
          
          if (settings.whatsappEnabled) {
            const deliveredMaintenance: MaintenanceRecord = {
              ...maintenance,
              status: MaintenanceStatus.DELIVERED,
              finalCost: totalFinalCost,
              deliveredAt: new Date()
            };

            const messageSent = await hybridWhatsAppService.sendDeviceCompletedMessage(deliveredMaintenance);
            if (messageSent) {
              console.log('✅ تم إرسال رسالة واتساب للعميل بنجاح');
            } else {
              console.warn('⚠️ فشل في إرسال رسالة واتساب للعميل');
            }
          }
        } catch (whatsappError) {
          console.error('❌ خطأ في إرسال رسالة واتساب:', whatsappError);
          // لا نريد إيقاف العملية إذا فشل إرسال الرسالة
        }
      } catch (whatsappError) {
        console.error('❌ خطأ في إرسال رسالة واتساب:', whatsappError);
        // لا نريد إيقاف العملية إذا فشل إرسال الرسالة
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error processing delivery payment:', error);
      setError('حدث خطأ أثناء معالجة التسليم والتسديد');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {isReturn ? <RefundIcon color="warning" /> : <DeliveryIcon color="primary" />}
          <Typography variant="h6">
            {isReturn ? 'إرجاع الجهاز كمرتجع' : 'تسليم الجهاز والتسديد النهائي (نقدي أو دين)'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box py={1}>
          {/* معلومات الطلب */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              معلومات الطلب
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>العميل:</strong> {maintenance.customerName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>نوع الجهاز:</strong> {maintenance.deviceType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة المبدئية:</strong> {formatCurrency(costSummary.initialCost)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>التكلفة المقدرة:</strong> {formatCurrency(costSummary.estimatedCost)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* خيار المرتجع */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isReturn}
                onChange={(e) => {
                  setIsReturn(e.target.checked);
                  if (e.target.checked) {
                    setPaymentAmount(0);
                    setHasExtraWages(false);
                    setExtraWagesAmount(0);
                  } else {
                    setIsRejectedReturn(false);
                  }
                  setError(null);
                }}
                color="warning"
                disabled={loading}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="warning.main" fontWeight="bold">
                  🔄 مرتجع - لا يتم تسديد أي مبلغ
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {/* خيار مرتجع مع رفض الجهاز */}
          {isReturn && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isRejectedReturn}
                  onChange={(e) => setIsRejectedReturn(e.target.checked)}
                  color="error"
                  disabled={loading}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="error.main" fontWeight="bold">
                    ❌ مرتجع مع رفض الجهاز (لا يُخصم من الفني)
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, ml: 3 }}
            />
          )}

          {isReturn && (
            <Alert severity={isRejectedReturn ? "error" : "warning"} sx={{ mb: 2 }}>
              <strong>تنبيه:</strong> عند اختيار "مرتجع"، سيتم تغيير حالة الطلب إلى "مرتجع" بدون أي تسديد مالي.
              <br />
              {isRejectedReturn ? (
                <small><strong>مرتجع مع رفض الجهاز:</strong> لن يتم خصم أجر الفني لأن الجهاز مرفوض.</small>
              ) : (
                <small><strong>مرتجع عادي:</strong> سيتم خصم أجر الفني المرتبط بهذا الطلب.</small>
              )}
              <br />
              <small>يمكنك تغيير الفني المسؤول إذا لزم الأمر.</small>
            </Alert>
          )}

          {/* اختيار الفني */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>الفني المسؤول</InputLabel>
            <Select
              value={newTechnicianId}
              onChange={(e) => setNewTechnicianId(e.target.value)}
              disabled={loading}
              label="الفني المسؤول"
            >
              {technicians.map((tech) => (
                <MenuItem key={tech.id} value={tech.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <EngineeringIcon fontSize="small" />
                    <Typography>{tech.name}</Typography>
                    {tech.id === maintenance.technicianId && (
                      <Chip label="الحالي" size="small" color="primary" />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!isReturn && (
            <>
              {/* التكلفة النهائية */}
          <TextField
            label="التكلفة النهائية"
            type="number"
            fullWidth
            value={finalCost || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setFinalCost(Math.max(0, value));
              // تحديث مبلغ الدفع تلقائياً إذا كان نقدي
              if (paymentMethod === 'cash') {
                setPaymentAmount(Math.max(0, value));
              }
              setError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
              inputProps: { min: 0, step: 0.01 }
            }}
            helperText="التكلفة النهائية الفعلية للصيانة"
            error={!!error}
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* تفاصيل التكلفة */}
          {finalCost > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.light' }}>
              <Typography variant="subtitle2" color="info.dark" gutterBottom>
                تفاصيل التكلفة
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="info.dark">
                    <strong>تكلفة قطع الغيار:</strong> {formatCurrency(partsCost)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="info.dark">
                    <strong>تكلفة العمالة:</strong> {formatCurrency(laborCost)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* خيار الأجور الإضافية */}
          <FormControlLabel
            control={
              <Checkbox
                checked={hasExtraWages}
                onChange={(e) => {
                  setHasExtraWages(e.target.checked);
                  if (!e.target.checked) {
                    setExtraWagesAmount(0);
                  }
                  setError(null);
                }}
                color="success"
                disabled={loading}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="success.main" fontWeight="bold">
                  💰 أجور زيادة
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {hasExtraWages && (
            <>
              <TextField
                label="مبلغ الأجور الإضافية"
                type="number"
                fullWidth
                value={extraWagesAmount || ''}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  setExtraWagesAmount(Math.max(0, value));
                  // تحديث مبلغ الدفع تلقائياً إذا كان نقدي
                  if (paymentMethod === 'cash') {
                    setPaymentAmount(Math.max(0, finalCost + value));
                  }
                  setError(null);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon color="success" />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
                  inputProps: { min: 0, step: 0.01 }
                }}
                helperText="مبلغ إضافي يُضاف للتكلفة النهائية"
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>معلومة:</strong> سيتم إضافة مبلغ {formatCurrency(extraWagesAmount)} للتكلفة النهائية.
                <br />
                <strong>إجمالي التكلفة:</strong> {formatCurrency(totalFinalCost)}
              </Alert>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* طريقة الدفع */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>طريقة الدفع</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => {
                const method = e.target.value as MaintenancePaymentMethod;
                setPaymentMethod(method);
                if (method === 'cash') {
                  setPaymentAmount(totalFinalCost);
                }
                if (!electronicMethods.includes(method)) {
                  setWalletNumber('');
                }
              }}
              label="طريقة الدفع"
              disabled={loading}
            >
              <MenuItem value="cash">
                <Box display="flex" alignItems="center" gap={1}>
                  <CardIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">💵 نقدي</Typography>
                    <Typography variant="caption" color="text.secondary">
                      دفع جزئي أو كامل نقداً الآن
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="debt">
                <Box display="flex" alignItems="center" gap={1}>
                  <DebtIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">📋 دين آجل</Typography>
                    <Typography variant="caption" color="text.secondary">
                      بدون دفع نقدي - كامل المبلغ دين
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="partial">
                <Box display="flex" alignItems="center" gap={1}>
                  <ReceiptIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">💰 دفع جزئي</Typography>
                    <Typography variant="caption" color="text.secondary">
                      دفع جزء نقداً + الباقي دين تلقائي
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="vodafone_wallet">
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIphoneIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">📱 فودافون كاش</Typography>
                    <Typography variant="caption" color="text.secondary">
                      تحويل كامل أو جزئي - الباقي دين تلقائي
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="instapay">
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIphoneIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">💳 انستاباي</Typography>
                    <Typography variant="caption" color="text.secondary">
                      تحويل بنكي كامل أو جزئي - الباقي دين تلقائي
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="etisalat_wallet">
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIphoneIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2">📲 اتصالات كاش</Typography>
                    <Typography variant="caption" color="text.secondary">
                      تحويل كامل أو جزئي - الباقي دين تلقائي
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* توضيح طريقة الدفع المختارة */}
          <Alert
            severity={paymentMethod === 'debt' ? 'warning' : 'info'}
            sx={{ mb: 2 }}
            icon={paymentMethod === 'debt' ? '📋' : '💡'}
          >
            <AlertTitle>
              {paymentMethod === 'debt' ? 'دين آجل' :
               paymentMethod === 'partial' ? 'دفع جزئي' :
               'دفع فوري'}
            </AlertTitle>
            {paymentMethod === 'debt' ? (
              <Typography variant="body2">
                <strong>لن يتم دفع أي مبلغ نقدي الآن.</strong><br/>
                • كامل المبلغ ({formatCurrency(finalCost)}) سيتم تسجيله كدين على العميل<br/>
                • تاريخ الاستحقاق: 30 يوم من اليوم<br/>
                • لا يؤثر على الخزينة النقدية اليوم
              </Typography>
            ) : paymentMethod === 'partial' ? (
              <Typography variant="body2">
                <strong>دفع جزء من المبلغ نقداً الآن.</strong><br/>
                • المبلغ المدفوع سيُضاف للخزينة النقدية<br/>
                • المبلغ المتبقي سيتم تسجيله كدين تلقائياً<br/>
                • يمكن تحصيل الدين لاحقاً من صفحة الديون
              </Typography>
            ) : electronicMethods.includes(paymentMethod) ? (
              <Typography variant="body2">
                <strong>تحويل إلكتروني كامل أو جزئي.</strong><br/>
                • يمكنك تحويل أي مبلغ من 0 إلى {formatCurrency(finalCost)}<br/>
                • المبلغ المحول سيُضاف للخزينة (يُحسب كنقدي)<br/>
                • أي مبلغ متبقي سيتم تسجيله كدين تلقائياً<br/>
                • <strong>مطلوب:</strong> رقم المحفظة أو الحساب المحول عليه
              </Typography>
            ) : (
              <Typography variant="body2">
                <strong>دفع نقدي كامل أو جزئي.</strong><br/>
                • المبلغ المدفوع سيُضاف للخزينة النقدية<br/>
                • أي مبلغ متبقي سيتم تسجيله كدين تلقائياً
              </Typography>
            )}
          </Alert>

          {/* مبلغ الدفع */}
          <TextField
            label="المبلغ المدفوع"
            type="number"
            fullWidth
            value={paymentMethod === 'debt' ? 0 : (paymentAmount || '')}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              setPaymentAmount(Math.max(0, Math.min(value, totalFinalCost)));
              setError(null);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MoneyIcon color={paymentMethod === 'debt' ? 'disabled' : 'success'} />
                </InputAdornment>
              ),
              endAdornment: <InputAdornment position="end">جنيه</InputAdornment>,
              inputProps: { min: 0, max: totalFinalCost, step: 0.01 }
            }}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                color: paymentMethod === 'debt' ? 'text.disabled' : 'text.primary',
                fontWeight: paymentMethod === 'debt' ? 'normal' : 'bold'
              },
              '& .MuiInputLabel-root': {
                color: paymentMethod === 'debt' ? 'text.disabled' : 'primary.main'
              }
            }}
            helperText={
              paymentMethod === 'debt'
                ? '🔒 الدين الآجل: لا يتم دفع أي مبلغ نقدي الآن - كامل المبلغ سيكون دين'
                : electronicMethods.includes(paymentMethod)
                ? `📱 يمكنك تحويل أي مبلغ من 0 إلى ${formatCurrency(totalFinalCost)} - الباقي سيكون دين تلقائياً`
                : `💵 يمكنك دفع أي مبلغ من 0 إلى ${formatCurrency(totalFinalCost)} - الباقي سيكون دين تلقائياً`
            }
            disabled={loading || paymentMethod === 'debt'}
          />

          {/* رقم المحفظة */}
          {electronicMethods.includes(paymentMethod) && (
            <>
              <TextField
                label="رقم المحفظة/الحساب المحول عليه"
                fullWidth
                value={walletNumber}
                onChange={e => setWalletNumber(e.target.value)}
                placeholder={
                  paymentMethod === 'vodafone_wallet' ? '01xxxxxxxxx' :
                  paymentMethod === 'instapay' ? '1234567890123456' :
                  '01xxxxxxxxx'
                }
                sx={{ mb: 1 }}
                disabled={loading}
                required
                helperText={`مطلوب لتأكيد ${
                  paymentMethod === 'vodafone_wallet' ? 'تحويل فودافون كاش' :
                  paymentMethod === 'instapay' ? 'التحويل البنكي' :
                  'تحويل اتصالات كاش'
                }`}
              />

              {/* مثال توضيحي للتحويل الجزئي */}
              <Alert severity="info" sx={{ mb: 2 }} icon="💡">
                <Typography variant="body2">
                  <strong>مثال:</strong> إذا كان لديك {formatCurrency(Math.min(finalCost * 0.6, finalCost))} في المحفظة من أصل {formatCurrency(finalCost)}:
                  <br />
                  • حول {formatCurrency(Math.min(finalCost * 0.6, finalCost))} الآن
                  • الباقي {formatCurrency(finalCost - Math.min(finalCost * 0.6, finalCost))} سيكون دين تلقائياً
                  • يمكنك دفع الدين لاحقاً من صفحة الديون
                </Typography>
              </Alert>
            </>
          )}

          {/* ملخص الدفع */}
          {totalFinalCost > 0 && !isReturn && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.light' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                ملخص الدفع
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>التكلفة الأساسية:</strong> {formatCurrency(finalCost)}
                  </Typography>
                </Grid>
                {hasExtraWages && extraWagesAmount > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="success.dark">
                      <strong>الأجور الإضافية:</strong> {formatCurrency(extraWagesAmount)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="body2" color="success.dark" fontWeight="bold">
                    <strong>إجمالي التكلفة:</strong> {formatCurrency(totalFinalCost)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>المبلغ المدفوع:</strong> {formatCurrency(actualPaymentAmount)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color={remainingAfterPayment > 0 ? "warning.dark" : "success.dark"}>
                    <strong>المبلغ المتبقي:</strong> {formatCurrency(remainingAfterPayment)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="success.dark">
                    <strong>طريقة الدفع:</strong> {
                      paymentMethod === 'cash' ? 'نقدي' :
                      paymentMethod === 'debt' ? 'دين' :
                      paymentMethod === 'partial' ? 'جزئي' :
                      paymentMethod === 'vodafone_wallet' ? 'محفظة فودافون' :
                      paymentMethod === 'instapay' ? 'انستاباي' : 'اتصالات كاش'
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* تأثير على الخزينة */}
          {totalFinalCost > 0 && !isReturn && (
            <Alert
              severity={paymentAmount > 0 ? "info" : "warning"}
              sx={{ mb: 2 }}
              icon={<MoneyIcon />}
            >
              <Typography variant="subtitle2" gutterBottom>
                💰 تأثير على خزينة اليوم:
              </Typography>
              {paymentAmount > 0 ? (
                <Typography variant="body2">
                  <strong>+ {formatCurrency(paymentAmount)} {
                    electronicMethods.includes(paymentMethod) ? 'تحويل إلكتروني' : 'نقدي'
                  }</strong> سيُضاف للخزينة
                  {remainingAfterPayment > 0 && (
                    <span> و <strong>{formatCurrency(remainingAfterPayment)} دين تلقائي</strong> (لا يؤثر على النقدي)</span>
                  )}
                  {electronicMethods.includes(paymentMethod) && (
                    <>
                      <br />
                      <small>💡 التحويلات الإلكترونية تُحسب كنقدي في الخزينة</small>
                    </>
                  )}
                </Typography>
              ) : (
                <Typography variant="body2">
                  <strong>+ 0 جنيه</strong> (دين تلقائي كامل - لا يؤثر على الخزينة النقدية)
                </Typography>
              )}
            </Alert>
          )}

          {/* تحذير الدين التلقائي */}
          {remainingAfterPayment > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>تنبيه:</strong> سيتم إنشاء سجل دين تلقائياً بمبلغ {formatCurrency(remainingAfterPayment)} مع تاريخ استحقاق 30 يوم من اليوم
              <br />
              <small>يمكن إدارة هذا الدين لاحقاً من صفحة الديون</small>
            </Alert>
          )}
          </>
          )}

          {/* رسالة خطأ */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={isReturn ? "warning" : "primary"}
          disabled={loading || (!isReturn && totalFinalCost <= 0)}
          startIcon={loading ? <CircularProgress size={20} /> : (isReturn ? <RefundIcon /> : <DeliveryIcon />)}
        >
          {loading ? 'جاري المعالجة...' : (isReturn ? 'تسجيل كمرتجع' : 'تسليم الجهاز')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryPaymentDialog;
